import {
  DEV_SCRATCH_DOC,
  DEV_SCRATCH_ITEMS_KEY,
  DEV_SCRATCH_ORDER_KEY,
  syncDocNameSchema,
  type DevScratchItem,
} from '@awwyeah/shared';
import { useState } from 'react';
import * as Y from 'yjs';
import { getDocManager, useSyncStatus, useYArray, useYDoc, useYMap } from '../../sync';

/** One item = one nested `Y.Map`, so two people editing different fields both keep theirs. */
type ItemMap = Y.Map<string | boolean>;

function readItem(id: string, map: ItemMap | undefined): DevScratchItem | null {
  if (!map) return null;
  return {
    id,
    text: typeof map.get('text') === 'string' ? (map.get('text') as string) : '',
    checked: map.get('checked') === true,
    by: typeof map.get('by') === 'string' ? (map.get('by') as string) : '',
    at: typeof map.get('at') === 'string' ? (map.get('at') as string) : '',
  };
}

/**
 * `/dev/sync?doc=dev:whatever` opens a scratch doc other than the default one. The WS-8
 * torture suite gives every scenario its own doc so scenarios cannot contaminate each
 * other; humans just visit `/dev/sync` and get the shared `dev:scratch`. Only `dev:`-prefixed
 * names are accepted, so this can never be used to poke at real household docs.
 */
function requestedDocName(): string {
  if (typeof window === 'undefined') return DEV_SCRATCH_DOC;
  const requested = new URLSearchParams(window.location.search).get('doc');
  if (!requested?.startsWith('dev:')) return DEV_SCRATCH_DOC;
  return syncDocNameSchema.safeParse(requested).success ? requested : DEV_SCRATCH_DOC;
}

/**
 * TEMPORARY dev page (WS-1) at `/dev/sync`.
 *
 * It exists to prove the sync layer by hand and to give the WS-8 torture suite something
 * to drive: a shared list, live in two browsers, editable while offline. Every element the
 * suite needs carries a `data-testid`. Delete this module once Phase 1 features exist.
 */
export function SyncScratchPage(): JSX.Element {
  const [docName] = useState(requestedDocName);
  const doc = useYDoc(docName);
  const status = useSyncStatus();
  const { map: itemsById } = useYMap<ItemMap>(doc, DEV_SCRATCH_ITEMS_KEY);
  const { array: order, items: orderedIds } = useYArray<string>(doc, DEV_SCRATCH_ORDER_KEY);
  const [draft, setDraft] = useState('');
  const [transportEnabled, setTransportEnabled] = useState(true);
  const me = String(doc.clientID);

  // Order is the source of truth for what is rendered: an id that lost its map (deleted
  // concurrently elsewhere) simply drops out, and a map with no id in `order` is gone.
  const items = orderedIds
    .map((id) => readItem(id, itemsById.get(id)))
    .filter((item): item is DevScratchItem => item !== null);

  /** Field-level write, so a concurrent edit to another field of the same item survives. */
  const patch = (id: string, apply: (item: ItemMap) => void): void => {
    const item = itemsById.get(id);
    if (!item) return;
    doc.transact(() => {
      apply(item);
      item.set('by', me);
      item.set('at', new Date().toISOString());
    });
  };

  const addItem = (): void => {
    const text = draft.trim();
    if (!text) return;
    // Writes go straight to the Yjs types: local, instant, conflict-free.
    const id = `${me}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const item: ItemMap = new Y.Map();
    doc.transact(() => {
      item.set('text', text);
      item.set('checked', false);
      item.set('by', me);
      item.set('at', new Date().toISOString());
      itemsById.set(id, item);
      order.push([id]);
    });
    setDraft('');
  };

  const toggle = (id: string): void => {
    patch(id, (item) => item.set('checked', item.get('checked') !== true));
  };

  const rename = (id: string, text: string): void => {
    patch(id, (item) => item.set('text', text));
  };

  const remove = (id: string): void => {
    doc.transact(() => {
      // Index is resolved against the live doc, never a stale render — a remote insert
      // landing between render and click can no longer make this delete the wrong row.
      const index = order.toArray().indexOf(id);
      if (index >= 0) order.delete(index, 1);
      itemsById.delete(id);
    });
  };

  const toggleTransport = (): void => {
    const manager = getDocManager();
    if (transportEnabled) manager.disconnectAll();
    else manager.reconnectAll();
    setTransportEnabled(!transportEnabled);
  };

  return (
    <section className="space-y-6" data-testid="sync-scratch-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Sync scratch list</h2>
          <p className="text-sm text-slate-500">
            Doc <code data-testid="doc-name">{docName}</code> · client{' '}
            <span data-testid="client-id">{me}</span>
          </p>
        </div>
        {/* The sync badge lives in the app chrome now (one per app, every route) — see
            apps/web/src/chrome/SyncStatusIndicator.tsx. `status-text` below stays as this
            page's own machine-readable copy of the same state. */}
      </header>

      <div className="flex gap-2">
        <input
          data-testid="add-input"
          aria-label="New item"
          className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Add an item…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') addItem();
          }}
        />
        <button
          data-testid="add-button"
          type="button"
          onClick={addItem}
          className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white"
        >
          Add
        </button>
      </div>

      <ul className="divide-y divide-slate-200 rounded border border-slate-200" data-testid="items">
        {items.length === 0 ? (
          <li className="px-3 py-6 text-center text-sm text-slate-400" data-testid="items-empty">
            No items yet
          </li>
        ) : (
          items.map((item) => (
            <li
              key={item.id}
              data-testid="item"
              data-item-id={item.id}
              data-checked={item.checked}
              className="flex items-center gap-3 px-3 py-2"
            >
              <input
                data-testid="item-checkbox"
                type="checkbox"
                aria-label={`Toggle ${item.text}`}
                checked={item.checked}
                onChange={() => toggle(item.id)}
                className="h-5 w-5"
              />
              {/* Editable on purpose: renaming an item is the second thing anyone does to a
                  list, and it is the case where field-level merging has to hold up. */}
              <input
                data-testid="item-text"
                aria-label={`Rename ${item.text}`}
                value={item.text}
                onChange={(event) => rename(item.id, event.target.value)}
                className={
                  item.checked
                    ? 'flex-1 bg-transparent text-slate-400 line-through outline-none'
                    : 'flex-1 bg-transparent outline-none'
                }
              />
              <button
                data-testid="item-delete"
                type="button"
                aria-label={`Delete ${item.text}`}
                onClick={() => remove(item.id)}
                className="text-xs text-slate-400 hover:text-red-600"
              >
                delete
              </button>
            </li>
          ))
        )}
      </ul>

      <footer className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
        <span data-testid="item-count">{items.length} items</span>
        <button
          data-testid="offline-toggle"
          data-transport={transportEnabled ? 'on' : 'off'}
          type="button"
          onClick={toggleTransport}
          className="rounded border border-slate-300 px-3 py-1"
        >
          {transportEnabled ? 'Simulate offline' : 'Go back online'}
        </button>
        <span data-testid="status-text">{status}</span>
      </footer>
    </section>
  );
}
